import React, { useContext, useMemo, useRef } from 'react';
import { AppShortContext } from '../../context/AppContext';
import { seams } from '../../constants';
import { getCostBlock } from '../Cost';
import { CurrentBadge } from '../CurrentBadge';
import style from '../Plans/Plan.module.scss';
import paywallStyle from './Paywall.module.scss';
import classnames from 'classnames';
import { getName } from '../../utils/getPlanName';
import PlanFeatures from '../Plans/PlanFeatures';
import { IPlan } from '../../model';
import piggy_bank from '../../assets/images/piggy_bank.gif';
import { getSportName } from '../../utils/getSportName';
import { useAuth } from '../../context/Auth.context';

interface IProps {
  plan: IPlan;
  totalPrice?: number;
  showSportBadge?: boolean;
}

const PaywallPlan = ({plan, totalPrice, showSportBadge}: IProps) => {
  const {
    baseCheckoutPath,
    yearly
  } = useContext(AppShortContext);
  const {isAuthenticated} = useAuth();

  const ref = useRef<HTMLDivElement>(null)

  const isSeams = useMemo(() => seams?.includes(String(plan?.plan_id)), [plan]);
  const currentPrice = plan?.current?.price;

  const yearAmount = plan?.current?.interval === 'year'
    ? currentPrice
      ? Number(currentPrice)
      : plan?.prices?.yearly?.unit_amount ?? 0
    : plan?.prices?.yearly?.unit_amount ?? 0;

  const monthAmount = plan?.current?.interval === 'month'
    ? currentPrice
      ? Number(currentPrice)
      : plan?.prices?.monthly?.unit_amount ?? 0
    : plan?.prices?.monthly?.unit_amount ?? 0;

  let costBlock;
  if(!monthAmount && !yearAmount) { costBlock = <></>; } else {
    if (totalPrice && !plan.current) {
      const month = yearly ? (yearAmount / 12) : monthAmount;
      if (totalPrice < month) {
        costBlock = getCostBlock({
          yearly: false,
          yearAmount: yearAmount,
          monthAmount: month - totalPrice,
          showDescription: true,
          className: classnames(paywallStyle.cost, paywallStyle.cost__plus),
          priceClassName: paywallStyle.price,
          descriptionClassName: paywallStyle.description
        })
      } else {
        costBlock = getCostBlock({
          yearly: false,
          yearAmount: yearAmount,
          monthAmount: month,
          showDescription: true,
          className: classnames(paywallStyle.cost),
          priceClassName: paywallStyle.price,
          descriptionClassName: paywallStyle.description
        })
      }
    } else {
      costBlock = getCostBlock({
        yearly,
        yearAmount: yearAmount,
        monthAmount: monthAmount,
        showDescription: true,
        className: classnames(paywallStyle.cost),
        priceClassName: paywallStyle.price,
        descriptionClassName: paywallStyle.description
      })
    }
  }

  const wpBadge = useMemo(() => {
    // @ts-ignore
    return window.styles?.[plan.plan_id];
  }, []);

  const priceObject = !yearly ? plan?.prices?.monthly : plan?.prices?.yearly;

  const currentBadge = CurrentBadge({plan, yearly, isAdditional: false, className: paywallStyle.current__badge});

  const showWpBadge = Boolean(!currentBadge && wpBadge?.text);

  return (
    <div className={classnames(style.plan_wrapper, paywallStyle.plan__wrapper)}>
      <div
        className={classnames(
          style.plan,
          {[style.plan_current]: currentBadge},
          {[style.seams]: isSeams},
          !currentBadge && wpBadge?.style ? {[style[wpBadge?.style]]: wpBadge?.style && wpBadge?.style !== 'default'} : {},
          {[style.wp_badge]: showWpBadge},
          paywallStyle.plan
        )}
        data-plan-id={plan.plan_id}>
        {showSportBadge ? <div className={paywallStyle.sport__badge}>{getSportName(plan.display.sport_category)}</div> : ""}
        {showWpBadge ? <div className={classnames(style.wp_badge_item, paywallStyle.wp_badge_item)}>{wpBadge?.text}</div> : ''}
        <div className={classnames(style.plan_top_part, paywallStyle.plan_top_part)} ref={ref}>
          {currentBadge}
          <h4 className={classnames(style.title, paywallStyle.plan__title)}>
            {getName(plan, isSeams)}
          </h4>
          {costBlock}
        </div>

        {plan.current ? <div className={classnames(paywallStyle.plan__current)}>Your plan</div> : <></>}

        <>{!plan?.current ? <div className={classnames(style.upgrade, paywallStyle.upgrade)}>
          <a
            href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}`}
            className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button, paywallStyle.upgrade_button)}>
            Buy
          </a>
        </div> : <></>}</>
        <div className={paywallStyle.plan__bottom}>
          {plan && (plan.features?.length || window?.shortDescriptions?.[plan.plan_id]) ? (<>
            <hr className={style.divider}/>
            <PlanFeatures plan={plan}/>
          </>) : <></>}
          {!plan?.current ? <div className={classnames(style.upgrade)}>
            <a
              href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}`}
              className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
              {isAuthenticated ? 'Upgrade Now' : 'Subscribe Now!'}
            </a>
          </div> : (plan.current.interval === 'month' && yearly ? <div className={style.upgrade}>
            <a href={`${baseCheckoutPath}?price_id=${plan?.prices?.yearly?.price_id}`}
               className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
              <img src={piggy_bank} alt="" style={{width: 24, height: 24, margin: '-5px 5px 0 -15px'}}/>
              Upgrade to Annual Plan
            </a>
          </div> : <></>)}
        </div>
      </div>
    </div>

  );
};

export default PaywallPlan;
